'use client';

import { IPrice } from '@/api/types/public';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { EPlatform } from '@/utils/enums/platform-enum';
import { env } from '@/utils/env';
import { priceInfo } from '@/utils/price';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import ActivityBanner from '../components/ActivityBanner';
import ActivityProductGrid from '../components/ActivityProductGrid';
import FaqAccordion from '../components/FaqAccordion';
import PgDescriptionA from './components/PgDescriptionA';
import PgIntroA from './components/PgIntroA';
import PgListIntro from './components/PgListIntro';
import PgSwiper from './components/PgSwiper';
import usePageTrack from './hooks/usePageTrack';

interface PageData {
  banner?: any;
  description?: any;
  pg_intro?: any;
  pg_swiper?: any;
  product_group_list?: any[][]; // 商品组列表数据
  recommend_config?: any;
  faq?: any;
  [key: string]: any;
}
export interface IProduct {
  id?: number | string;
  short_name?: string;
  price_info: IPrice[];
  price_info_v0: IPrice[];
  price_info_v1: IPrice[];
  price_info_v2: IPrice[];
  price_info_v3: IPrice[];
  config_image?: string;
  config_video?: string;
  name: string;
  [key: string]: any;
}

interface ClientPageProps {
  serverData: PageData | null;
}

export default function PGClientPage({ serverData }: ClientPageProps) {
  // 直接使用serverData作为pageData
  const pageData = serverData;

  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);

  // App相关状态管理
  const { deviceType } = useContext(ResponsiveContext);
  // 使用deviceType判断是否在App内
  const isInApp = [EPlatform.IOS, EPlatform.ANDROID].includes(deviceType);

  // 缓存Banner数据，避免每次都传递新对象
  const bannerData = useMemo(() => pageData?.banner, [pageData?.banner]);

  // 设置各区域的曝光检测
  const {
    bannerRef,
    trackPageView,
    trackPageLeave,
    trackBannerClick,
    trackProduct,
    trackPageMktClick,
  } = usePageTrack({
    banner: bannerData,
    page_name: 'PG专题页A版',
  });

  // 商品列表加载状态
  const [isProductListComplete, setIsProductListComplete] = useState(false);

  // 缓存aspectRatio，避免每次都计算
  const bannerAspectRatio = useMemo(
    () => pageData?.banner?.aspectRatio,
    [pageData?.banner?.aspectRatio],
  );
  // * 商品曝光/点击埋点
  const reportProduct = (
    product: IProduct,
    index: number,
    type: 'exp' | 'click' = 'exp',
    module_name: string,
  ) => {
    const { short_name, id, sku, shop_sku, business_sku } = product;
    const nowPriceInfo = priceInfo(userInfo, product, currency);
    const basePriceInfo = priceInfo(userInfo, product, env.NEXT_PUBLIC_DEFAULT_CURRENCY);
    trackProduct(type, {
      module_name,
      pits_index: index,
      product_name: short_name,
      product_id: `${id}`,
      product_sku: sku,
      business_sku,
      shop_sku,
      product_price: parseFloat(nowPriceInfo.price),
      current_product_price: parseFloat(nowPriceInfo.sell_price),
      base_current_product_price: parseFloat(basePriceInfo.sell_price),
      currency: currency.name,
      dollar_exchange_rate: parseFloat(currency.exchange_rate),
    });
  };
  // 使用useCallback缓存回调函数，避免重复渲染
  const handleBannerClick = (data: any) => {
    logger.log('BannerB clicked:', data);
    trackBannerClick();
  };

  // 处理商品列表加载完成
  const handleProductListLoadComplete = useCallback((hasMore: boolean) => {
    // 当没有更多数据时，表示加载完成
    setIsProductListComplete(!hasMore);
  }, []);

  // APP内时监听appView和appLeave事件（埋点相关）
  useEffect(() => {
    if (isInApp) {
      window.addEventListener('appView', trackPageView);
      window.addEventListener('appLeave', trackPageLeave);
      return () => {
        window.removeEventListener('appView', trackPageView);
        window.removeEventListener('appLeave', trackPageLeave);
      };
    }
  }, [isInApp, trackPageView, trackPageLeave]);

  // 页面加载完成后触发页面浏览埋点
  useEffect(() => {
    if (pageData) {
      trackPageView();
    }
    return () => {
      trackPageLeave();
    };
  }, []);

  if (!pageData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">No data available</div>
      </div>
    );
  }

  return (
    <div className="bg-[#1E0B08]">
      {/* Banner 区域 */}
      {bannerData && (
        <div ref={bannerRef}>
          <ActivityBanner
            data={bannerData}
            aspectRatio={bannerAspectRatio}
            onBannerClick={handleBannerClick}
          />
        </div>
      )}

      {/* PgIntro 区域 */}
      {pageData?.pg_intro && (
        <div onClick={() => trackPageMktClick({ module_name: 'PG介绍' })}>
          <PgIntroA pgIntroData={pageData.pg_intro} />
        </div>
      )}

      {/* PgSwiper 区域 */}
      {pageData?.collection_list && (
        <div>
          <PgSwiper
            pgSwiperData={pageData.collection_list}
            productList={pageData.product_list_top || []}
            onProductClick={(product, index) => {
              logger.log('PG Swiper Product clicked:', product, index);
              reportProduct(product as unknown as IProduct, index, 'click', 'Our Collection');
            }}
            onProductExp={(product, index) => {
              logger.log('PG Swiper Product exposed:', product, index);
              reportProduct(product as unknown as IProduct, index, 'exp', 'Our Collection');
            }}
          />
        </div>
      )}

      {/* PgListIntro 区域 */}
      {pageData?.product_list && (
        <div>
          <PgListIntro productList={pageData.product_list} />
        </div>
      )}

      {/* Description 区域 */}
      {pageData?.pg_description && (
        <div onClick={() => trackPageMktClick({ module_name: 'PG描述' })}>
          <PgDescriptionA descriptionData={pageData.pg_description} />
        </div>
      )}

      {/* ProductList 区域 */}
      {pageData?.recommend_config && (
        <div className="bg-[#ffffff] rounded-t-[16px] sm:rounded-t-[0px]">
          <ActivityProductGrid
            title={pageData.recommend_config.title}
            apiConfig={{
              group_id: pageData.recommend_config.group_id,
              limit: 12,
              in_stock: 3,
              is_sale: 3,
            }}
            deviceType={deviceType}
            showInfiniteScroll
            padding={{ wap: 'px-[8px]', pc: 'px-[48px]' }}
            productCardProps={{
              showCorrelation: true,
            }}
            onProductClick={(product, index) => {
              // TODO: 添加产品点击埋点
              logger.log('PG Product clicked:', product, index);
              reportProduct(product as IProduct, index, 'click', '商品推荐');
            }}
            onProductExp={(product, index) => {
              // TODO: 添加产品曝光埋点
              logger.log('PG Product exposed:', product, index);
              reportProduct(product as IProduct, index, 'exp', '商品推荐');
            }}
            onLoadComplete={handleProductListLoadComplete}
          />
        </div>
      )}

      {/* FAQ 区域 - 对应Vue中的"FAQ"曝光，只有商品列表加载完成后才显示 */}
      {pageData?.faq && isProductListComplete && (
        <div>
          <FaqAccordion
            faqData={pageData.faq}
            className="!bg-[#F4F4F4]"
            onFaqClick={(params) =>
              trackPageMktClick({ module_name: 'FAQ', pits_index: params.pits_index })
            }
          />
        </div>
      )}
    </div>
  );
}
